import React, { useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Box,
  Dialog,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Toolbar,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';
import Api from '../../api/userApi';

const compactLocationText = (value) => {
  if (typeof value !== 'string') return '';
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
};

const matchesLocationQuery = (place, query) => {
  const raw = (place || '').toLowerCase();
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;
  if (raw.includes(q)) return true;
  const compactPlace = compactLocationText(place);
  const compactQuery = compactLocationText(query);
  return Boolean(compactQuery && compactPlace.includes(compactQuery));
};

const LocationSelectDialog = ({
  open,
  onClose,
  villages = [],
  selectedLocation = '',
  onSelectLocation
}) => {
  const [searchText, setSearchText] = useState('');
  const [apiVillages, setApiVillages] = useState([]);

  useEffect(() => {
    if (!open) return undefined;
    setSearchText(selectedLocation || '');
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(Api.workerVillages);
        if (!cancelled) {
          setApiVillages(Array.isArray(response.data?.villages) ? response.data.villages : []);
        }
      } catch (error) {
        console.error('Error fetching locations:', error);
        if (!cancelled) setApiVillages([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, selectedLocation]);

  const locationOptions = useMemo(() => {
    const merged = [...villages, ...apiVillages]
      .map((place) => (typeof place === 'string' ? place.trim() : ''))
      .filter(Boolean);
    return Array.from(new Set(merged));
  }, [villages, apiVillages]);

  const filteredLocations = useMemo(() => {
    const query = searchText.trim();
    if (!query) return locationOptions;
    return locationOptions.filter((place) => matchesLocationQuery(place, query));
  }, [searchText, locationOptions]);

  const typedQuery = searchText.trim();
  const typedIsDistinct = Boolean(
    typedQuery &&
      !filteredLocations.some((place) => place.toLowerCase() === typedQuery.toLowerCase())
  );

  const handleSelect = (value) => {
    const next = (value || '').trim();
    if (!next) return;
    if (onSelectLocation) onSelectLocation(next);
    if (onClose) onClose();
  };

  const handleSubmitTyped = () => {
    if (typedQuery) handleSelect(typedQuery);
  };

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { bgcolor: '#f9f5f1' }
      }}
    >
      <AppBar
        elevation={0}
        sx={{
          position: 'sticky',
          top: 0,
          bgcolor: '#221b47'
        }}
      >
        <Toolbar>
          <Typography
            variant="h6"
            sx={{ flex: 1, fontWeight: 600, color: '#fff' }}
          >
            Search Location
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2 }}>
        <TextField
          fullWidth
          autoFocus
          type="search"
          placeholder="Type a location..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleSubmitTyped();
            }
          }}
          InputProps={{
            startAdornment: (
              <LocationOnIcon sx={{ mr: 1, color: '#6b6b6b' }} />
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="search workers in this location"
                  edge="end"
                  onClick={handleSubmitTyped}
                  disabled={!typedQuery}
                >
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              bgcolor: '#fff',
              borderRadius: 2
            }
          }}
        />

        <List
          dense
          sx={{
            bgcolor: '#fff',
            borderRadius: 2,
            maxHeight: '70vh',
            overflow: 'auto'
          }}
        >
          {typedIsDistinct && (
            <ListItemButton onClick={handleSubmitTyped}>
              <SearchIcon sx={{ mr: 1, color: '#ff7a1a' }} />
              <ListItemText
                primary={`Search workers in “${typedQuery}”`}
                secondary="Use this location"
              />
            </ListItemButton>
          )}

          {filteredLocations.map((place) => (
            <ListItemButton
              key={place}
              onClick={() => handleSelect(place)}
              selected={place === selectedLocation}
            >
              <LocationOnIcon sx={{ mr: 1, color: '#ff7a1a' }} />
              <ListItemText primary={place} />
            </ListItemButton>
          ))}

          {!typedQuery && filteredLocations.length === 0 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ color: '#7b7b7b' }}>
                Type a location to search workers.
              </Typography>
            </Box>
          )}
        </List>
      </Box>
    </Dialog>
  );
};

export default LocationSelectDialog;
